import { useState, useEffect } from "react";
import {
  getOptimizerDefaults,
  submitOptimization,
  type OptimizerDefaults,
} from "../../services/optimizerService";

interface Constraint {
  id: string;
  metric: string;
  operator: string;
  value: number;
}

interface OptimizationConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  strategyCode: string;
  strategyName: string;
  currentMetrics?: {
    cagr?: number;
    sharpe?: number;
    max_drawdown?: number;
    sortino?: number;
  };
  onSubmit?: (jobId: string) => void;
}

const METRIC_OPTIONS = [
  { value: "cagr", label: "CAGR" },
  { value: "sharpe", label: "Sharpe Ratio" },
  { value: "max_drawdown", label: "Max Drawdown" },
  { value: "sortino", label: "Sortino Ratio" },
];

const OPERATOR_OPTIONS = [
  { value: ">=", label: ">=" },
  { value: "<=", label: "<=" },
];

function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export default function OptimizationConfigModal({
  isOpen,
  onClose,
  strategyCode,
  strategyName,
  currentMetrics,
  onSubmit,
}: OptimizationConfigModalProps) {
  // Target metric
  const [targetMetric, setTargetMetric] = useState("cagr");
  const [targetValue, setTargetValue] = useState<number | string>("");

  // Constraints
  const [constraints, setConstraints] = useState<Constraint[]>([]);

  // Advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [defaults, setDefaults] = useState<OptimizerDefaults | null>(null);
  const [advancedValues, setAdvancedValues] = useState<
    Partial<OptimizerDefaults>
  >({});

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch admin defaults on open
  useEffect(() => {
    if (isOpen && !defaults) {
      getOptimizerDefaults()
        .then((d) => {
          setDefaults(d);
          setAdvancedValues({ ...d });
        })
        .catch((err) =>
          console.error("Failed to load optimizer defaults:", err),
        );
    }
  }, [isOpen, defaults]);

  // Pre-populate target value and constraints from current metrics
  useEffect(() => {
    if (isOpen && currentMetrics) {
      // Set a sensible target value based on current metric
      if (currentMetrics.cagr !== undefined && targetMetric === "cagr") {
        setTargetValue(
          Math.round((currentMetrics.cagr * 1.2 + Number.EPSILON) * 10) / 10,
        );
      }

      // Suggest constraints based on current metrics
      const suggested: Constraint[] = [];
      if (currentMetrics.max_drawdown !== undefined) {
        suggested.push({
          id: generateId(),
          metric: "max_drawdown",
          operator: ">=",
          value:
            Math.round(
              (currentMetrics.max_drawdown + Number.EPSILON) * 10,
            ) / 10,
        });
      }
      if (currentMetrics.sharpe !== undefined) {
        suggested.push({
          id: generateId(),
          metric: "sharpe",
          operator: ">=",
          value:
            Math.round((currentMetrics.sharpe + Number.EPSILON) * 100) / 100,
        });
      }
      setConstraints(suggested);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSubmitting(false);
      setShowAdvanced(false);
    }
  }, [isOpen]);

  const addConstraint = () => {
    // Find the first metric not already used as a constraint
    const usedMetrics = new Set(constraints.map((c) => c.metric));
    const available = METRIC_OPTIONS.find(
      (m) => !usedMetrics.has(m.value),
    );
    if (!available) return;

    setConstraints([
      ...constraints,
      {
        id: generateId(),
        metric: available.value,
        operator: ">=",
        value: 0,
      },
    ]);
  };

  const removeConstraint = (id: string) => {
    setConstraints(constraints.filter((c) => c.id !== id));
  };

  const updateConstraint = (
    id: string,
    field: keyof Constraint,
    value: string | number,
  ) => {
    setConstraints(
      constraints.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const numericTarget = Number(targetValue);
    if (!targetMetric || isNaN(numericTarget) || numericTarget === 0) {
      setError("Please select a target metric and enter a target value.");
      return;
    }

    if (!strategyCode.trim()) {
      setError("Strategy code is required.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Build preserve map from constraints
      // Engine expects max_drawdown as negative (e.g. -10 for 10% DD)
      const preserve: Record<string, number> = {};
      for (const c of constraints) {
        preserve[c.metric] =
          c.metric === "max_drawdown" ? -Math.abs(c.value) : c.value;
      }

      const result = await submitOptimization({
        strategy_code: strategyCode,
        target_metric: targetMetric,
        target_value:
          targetMetric === "max_drawdown"
            ? -Math.abs(numericTarget)
            : numericTarget,
        preserve: Object.keys(preserve).length > 0 ? preserve : undefined,
        source_metrics: currentMetrics
          ? {
              ...(currentMetrics.cagr !== undefined && {
                cagr: currentMetrics.cagr,
              }),
              ...(currentMetrics.sharpe !== undefined && {
                sharpe: currentMetrics.sharpe,
              }),
              ...(currentMetrics.max_drawdown !== undefined && {
                max_drawdown: -Math.abs(currentMetrics.max_drawdown),
              }),
              ...(currentMetrics.sortino !== undefined && {
                sortino: currentMetrics.sortino,
              }),
            }
          : undefined,
        population_size: advancedValues.population_size,
        max_generations: advancedValues.max_generations,
        max_evaluations: advancedValues.max_evaluations,
        max_tree_depth: advancedValues.max_tree_depth,
        crossover_prob: advancedValues.crossover_prob,
        mutation_prob: advancedValues.mutation_prob,
        stagnation_limit: advancedValues.stagnation_limit,
        initial_capital: advancedValues.initial_capital,
        overfit_threshold: advancedValues.overfit_threshold,
        max_candidates: advancedValues.max_candidates,
      });

      // Success — close modal and notify parent
      onClose();
      onSubmit?.(result.job_id);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to submit optimization",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close modal"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Modal */}
      <div className="relative bg-surface-900 border border-surface-700 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-700/50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center">
              <svg
                className="w-4 h-4 text-primary-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-surface-100">
              Optimize Strategy
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-surface-400 hover:text-surface-200 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Strategy Info */}
          <div>
            <div className="text-surface-100 font-medium">{strategyName}</div>
            {currentMetrics && (
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-surface-400">
                {currentMetrics.cagr !== undefined && (
                  <span>
                    CAGR{" "}
                    <span
                      className={
                        currentMetrics.cagr >= 0
                          ? "text-accent-400"
                          : "text-red-400"
                      }
                    >
                      {currentMetrics.cagr >= 0 ? "+" : ""}
                      {currentMetrics.cagr.toFixed(1)}%
                    </span>
                  </span>
                )}
                {currentMetrics.sharpe !== undefined && (
                  <span>
                    Sharpe{" "}
                    <span className="text-surface-200">
                      {currentMetrics.sharpe.toFixed(2)}
                    </span>
                  </span>
                )}
                {currentMetrics.max_drawdown !== undefined && (
                  <span>
                    MaxDD{" "}
                    <span className="text-red-400">
                      {currentMetrics.max_drawdown.toFixed(1)}%
                    </span>
                  </span>
                )}
                {currentMetrics.sortino !== undefined && (
                  <span>
                    Sortino{" "}
                    <span className="text-surface-200">
                      {currentMetrics.sortino.toFixed(2)}
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Target Metric */}
          <div>
            <label className="text-sm font-medium text-surface-200 mb-2 block">
              What to maximize
            </label>
            <div className="grid grid-cols-2 gap-3">
              <select
                value={targetMetric}
                onChange={(e) => setTargetMetric(e.target.value)}
                className="input-field"
              >
                {METRIC_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                type="number"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder="Target value"
                className="input-field"
              />
            </div>
          </div>

          {/* Constraints */}
          <div>
            <label className="text-sm font-medium text-surface-200 mb-2 block">
              Constraints to preserve
            </label>
            <div className="space-y-2">
              {constraints.map((c) => (
                <div key={c.id} className="flex items-center gap-2">
                  <select
                    value={c.metric}
                    onChange={(e) =>
                      updateConstraint(c.id, "metric", e.target.value)
                    }
                    className="input-field flex-1"
                  >
                    {METRIC_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={c.operator}
                    onChange={(e) =>
                      updateConstraint(c.id, "operator", e.target.value)
                    }
                    className="input-field w-20"
                  >
                    {OPERATOR_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={c.value}
                    onChange={(e) =>
                      updateConstraint(c.id, "value", Number(e.target.value))
                    }
                    className="input-field w-24"
                    step="any"
                  />
                  <button
                    onClick={() => removeConstraint(c.id)}
                    className="p-2 text-surface-400 hover:text-red-400 transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}
              {constraints.length < METRIC_OPTIONS.length - 1 && (
                <button
                  onClick={addConstraint}
                  className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add constraint
                </button>
              )}
            </div>
          </div>

          {/* Advanced Settings Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-surface-400 hover:text-surface-200 transition-colors"
          >
            <svg
              className={`w-4 h-4 transition-transform ${showAdvanced ? "rotate-90" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            Advanced Settings
          </button>

          {showAdvanced && defaults && (
            <div className="grid grid-cols-2 gap-3 p-4 rounded-xl bg-surface-800/30 border border-surface-700/30">
              <div className="col-span-2">
                <p className="text-xs text-surface-500 mb-3">
                  Override admin defaults for this optimization run.
                </p>
              </div>
              <div>
                <label className="text-xs text-surface-400 mb-1 block">
                  Population Size
                </label>
                <input
                  type="number"
                  value={advancedValues.population_size ?? ""}
                  onChange={(e) =>
                    setAdvancedValues({
                      ...advancedValues,
                      population_size: Number(e.target.value),
                    })
                  }
                  min={10}
                  max={200}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-surface-400 mb-1 block">
                  Max Generations
                </label>
                <input
                  type="number"
                  value={advancedValues.max_generations ?? ""}
                  onChange={(e) =>
                    setAdvancedValues({
                      ...advancedValues,
                      max_generations: Number(e.target.value),
                    })
                  }
                  min={5}
                  max={100}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-surface-400 mb-1 block">
                  Max Evaluations
                </label>
                <input
                  type="number"
                  value={advancedValues.max_evaluations ?? ""}
                  onChange={(e) =>
                    setAdvancedValues({
                      ...advancedValues,
                      max_evaluations: Number(e.target.value),
                    })
                  }
                  min={50}
                  max={2000}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-surface-400 mb-1 block">
                  Max Tree Depth
                </label>
                <input
                  type="number"
                  value={advancedValues.max_tree_depth ?? ""}
                  onChange={(e) =>
                    setAdvancedValues({
                      ...advancedValues,
                      max_tree_depth: Number(e.target.value),
                    })
                  }
                  min={5}
                  max={30}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-surface-400 mb-1 block">
                  Crossover Prob
                </label>
                <input
                  type="number"
                  value={advancedValues.crossover_prob ?? ""}
                  onChange={(e) =>
                    setAdvancedValues({
                      ...advancedValues,
                      crossover_prob: Number(e.target.value),
                    })
                  }
                  min={0}
                  max={1}
                  step={0.05}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-surface-400 mb-1 block">
                  Mutation Prob
                </label>
                <input
                  type="number"
                  value={advancedValues.mutation_prob ?? ""}
                  onChange={(e) =>
                    setAdvancedValues({
                      ...advancedValues,
                      mutation_prob: Number(e.target.value),
                    })
                  }
                  min={0}
                  max={1}
                  step={0.05}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-surface-400 mb-1 block">
                  Initial Capital ($)
                </label>
                <input
                  type="number"
                  value={advancedValues.initial_capital ?? ""}
                  onChange={(e) =>
                    setAdvancedValues({
                      ...advancedValues,
                      initial_capital: Number(e.target.value),
                    })
                  }
                  min={1000}
                  max={10000000}
                  step={1000}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-surface-400 mb-1 block">
                  Overfit Threshold
                </label>
                <input
                  type="number"
                  value={advancedValues.overfit_threshold ?? ""}
                  onChange={(e) =>
                    setAdvancedValues({
                      ...advancedValues,
                      overfit_threshold: Number(e.target.value),
                    })
                  }
                  min={0}
                  max={1}
                  step={0.05}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-surface-400 mb-1 block">
                  Stagnation Limit
                </label>
                <input
                  type="number"
                  value={advancedValues.stagnation_limit ?? ""}
                  onChange={(e) =>
                    setAdvancedValues({
                      ...advancedValues,
                      stagnation_limit: Number(e.target.value),
                    })
                  }
                  min={2}
                  max={20}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-surface-400 mb-1 block">
                  Max Candidates
                </label>
                <input
                  type="number"
                  value={advancedValues.max_candidates ?? ""}
                  onChange={(e) =>
                    setAdvancedValues({
                      ...advancedValues,
                      max_candidates: Number(e.target.value),
                    })
                  }
                  min={1}
                  max={20}
                  className="input-field text-sm"
                />
              </div>
            </div>
          )}

          {/* Info notice */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-primary-500/10 border border-primary-500/20">
            <svg
              className="w-4 h-4 text-primary-400 mt-0.5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-xs text-primary-300">
              Optimization typically takes 3-10 minutes. You&apos;ll be
              notified when results are ready.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
              <svg
                className="w-4 h-4 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-surface-700/50">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              submitting || !targetMetric || !targetValue || !strategyCode
            }
            className="btn-primary flex items-center gap-2"
          >
            {submitting && <span className="spinner w-4 h-4" />}
            Start Optimization
          </button>
        </div>
      </div>
    </div>
  );
}
